/**
 * O item do `/` que insere um gráfico.
 *
 * ## Por que este arquivo precisou existir
 *
 * O bloco `tuninho:chart` foi entregue no card 1850168865112196130 com o registro de **como
 * ele desenha** e sem nenhum registro de **como ele é inserido**. Medido no `dev` em
 * 2026-08-28: o menu do `/` abre com 10 grupos e 55 itens, e nenhum deles cria um gráfico.
 * O bloco renderiza quando já está no documento — e não havia como pôr um lá.
 *
 * É a **oitava** ocorrência da família que as regras 8.1 (`apps: []`), 8.4 (`profiles`
 * inerte), 8.18.1 (`destino` sem consumidor), 8.26 (chave sem escritor), 8.32 (porta sem
 * consumidor), 8.46.1 (hook não registrado) e 8.52 (flavour registrado sem `effect`)
 * catalogam. As duas últimas foram cometidas por este mesmo bloco, e é o caso que mais
 * ensina: **registrar um bloco tem mais de um ponto, e faltar um deles não falha — some.**
 *
 * ## Por que o gráfico nasce LIGADO a uma tabela
 *
 * Um gráfico com `sourceBlockId` vazio desenha o aviso *"Escolha a tabela que alimenta este
 * gráfico"* — e até este card **não havia onde escolher**. Entregar um item de menu que
 * produz isso seria a Regra 8.41: uma ação que parece resolver e devolve o mesmo sintoma,
 * com o agravante de consumir o gesto certo do operador.
 *
 * Então o item resolve a tabela ANTES de inserir, e cria uma se não houver. Um clique sempre
 * devolve um par tabela+gráfico coerente.
 *
 * ## Por que o grupo é `7_Database`
 *
 * O gráfico lê uma tabela; ele pertence ao lugar onde se procuram tabelas. Criar um grupo
 * `Financeiro` separado colocaria uma palavra do NOSSO domínio num menu que é do editor, e
 * quem quisesse um gráfico de qualquer outra coisa não o procuraria ali.
 */

import { getSelectedModelsCommand } from '@blocksuite/affine-shared/commands';
import { isInsideBlockByFlavour } from '@blocksuite/affine-shared/utils';
import { insertDatabaseBlockCommand } from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine/model';
import type { BlockModel } from '@blocksuite/affine/store';
import { type SlashMenuConfig } from '@blocksuite/affine/widgets/slash-menu';
import { ChartPanelIcon } from '@blocksuite/icons/lit';
import { viewPresets } from '@blocksuite/data-view/view-presets';

import { acharTabela, configuracaoInicial } from '../dados';
import { FLAVOUR_DO_GRAFICO } from '../model';

export const graficoSlashMenuConfig: SlashMenuConfig = {
  items: [
    {
      name: 'Chart',
      description: 'Um gráfico sobre uma tabela deste documento.',
      /*
       * ⚠️ `isFuzzyMatch` (`affine/shared/src/utils/string.ts:17`) casa **subsequência** e é
       * `toLowerCase()` — mas NÃO tira acento. Então `grafico` e `gráfico` são apelidos
       * DIFERENTES e os dois precisam estar aqui: quem digita `/gráfico` com acento não
       * casaria com `grafico`, e quem digita sem acento não casaria com `gráfico`.
       * Escrever só um dos dois é o defeito silencioso que este card veio consertar.
       */
      searchAlias: [
        'grafico', 'gráfico', 'graficos', 'gráficos',
        'chart', 'graph', 'financeiro', 'barras', 'linha', 'pizza',
      ],
      icon: ChartPanelIcon(),
      group: '7_Database@3',
      when: ({ model }) =>
        !isInsideBlockByFlavour(model.store, model, 'affine:edgeless-text'),
      action: ({ std, model }) => {
        std.command
          .chain()
          .pipe(getSelectedModelsCommand)
          .pipe((ctx, next) => {
            const store = std.store;
            let tabela = acharTabela<DatabaseBlockModel>(
              store as never,
              model as never
            );

            /*
             * ⚠️ **Onde o gráfico entra depende de quem criou a tabela.**
             *
             * Tabela que já existia pode estar longe do cursor — é o caso do `📈 DRE`, que
             * tem duas. Enfiar o gráfico ao lado DELA levaria o bloco para outro ponto do
             * documento, e quem digitou `/` veria o cursor ficar onde estava e nada aparecer
             * ali. O gráfico entra ONDE se pediu, e apenas LÊ a tabela de onde ela estiver.
             *
             * Tabela recém-criada é o oposto: ela nasceu logo abaixo do cursor, e o gráfico
             * tem de vir depois dela — senão fica entre o parágrafo e a própria tabela que
             * ele lê, o que se lê de cima para baixo como o contrário do que é.
             */
            let ancora: BlockModel = model;

            if (!tabela) {
              // `removeEmptyLine: false`, e não `true` como o item `Table View`: o parágrafo
              // do cursor é a âncora do bloco seguinte. Apagá-lo aqui tiraria o chão de
              // debaixo do gráfico — e o `addSiblingBlocks` num modelo removido não falha
              // alto, devolve vazio.
              const [ok, dados] = std.command.exec(insertDatabaseBlockCommand, {
                selectedModels: ctx.selectedModels,
                viewType: viewPresets.tableViewMeta.type,
                place: 'after',
                removeEmptyLine: false,
              });
              const novoId = ok ? dados.insertedDatabaseBlockId : undefined;
              const criada = novoId ? store.getBlock(novoId) : undefined;
              if (criada) {
                tabela = criada.model as DatabaseBlockModel;
                ancora = criada.model;
              }
            }

            if (!tabela) {
              // Não deu para achar nem criar tabela. Sair sem inserir é melhor que inserir um
              // gráfico órfão que pede o que ninguém pode dar (Regra 8.41).
              return;
            }

            const colunas = (tabela.props.columns$?.value ?? []).map(c => ({
              id: c.id,
              type: c.type,
              name: c.name,
            }));
            const { eixoX, series } = configuracaoInicial(colunas);

            store.addSiblingBlocks(
              ancora,
              [
                {
                  flavour: FLAVOUR_DO_GRAFICO,
                  sourceBlockId: tabela.id,
                  viewId: '',
                  tipo: 'barra',
                  eixoX,
                  series,
                  agregacao: 'soma',
                  titulo: '',
                  altura: 320,
                },
              ],
              'after'
            );
            next();
          })
          .run();
      },
    },
  ],
};
